import React, { useState } from 'react';
import { Head, router } from '@inertiajs/react';
import AdminLayout from '../../components/layout/AdminLayout';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Checkbox from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';

export default function CreditsIndex({ agency, creditPacks, creditHistory, statistics, filters = {} }) {
  const [selectedPeriod, setSelectedPeriod] = useState(filters.period || '');
  const [startDate, setStartDate] = useState(filters.startDate || '');
  const [endDate, setEndDate] = useState(filters.endDate || '');
  const [selectedPack, setSelectedPack] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(agency.autoRechargeEnabled);
  const [autoRechargePack, setAutoRechargePack] = useState(agency.autoRechargePack?.id);

  const handlePurchase = async (packId) => {
    if (purchaseLoading) return;

    if (!confirm(`Êtes-vous sûr de vouloir acheter ce pack de crédits ?`)) {
      return;
    }

    setPurchaseLoading(true);

    try {
      await router.post('/credits/purchase', {
        credit_pack_id: packId,
        payment_method: 'card'
      });
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleAutoRechargeUpdate = async () => {
    try {
      await router.put('/credits/auto_recharge', {
        enabled: autoRechargeEnabled,
        credit_pack_id: autoRechargePack,
        alert_threshold: agency.alertThreshold
      });
    } catch (error) {
      console.error('Failed to update auto-recharge:', error);
    }
  };

  const applyPeriodFilter = (period) => {
    setSelectedPeriod(period);
    if (period !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
    router.get('/credits', {
      period: period || undefined,
      start_date: period === 'custom' ? startDate : undefined,
      end_date: period === 'custom' ? endDate : undefined
    }, {
      preserveState: true,
      preserveScroll: true
    });
  };

  const applyCustomDateFilter = () => {
    router.get('/credits', {
      period: 'custom',
      start_date: startDate || undefined,
      end_date: endDate || undefined
    }, {
      preserveState: true,
      preserveScroll: true
    });
  };

  const clearFilters = () => {
    setSelectedPeriod('');
    setStartDate('');
    setEndDate('');
    router.get('/credits', {}, { preserveState: true });
  };

  const hasActiveFilters = selectedPeriod || startDate || endDate;

  const getMovementTypeLabel = (type) => {
    const labels = {
      purchase: 'Achat',
      deduction: 'Déduction',
      refund: 'Remboursement',
      bonus: 'Bonus',
      adjustment: 'Ajustement'
    };
    return labels[type] || type;
  };

  const getMovementTypeColor = (type) => {
    const colors = {
      purchase: 'text-green-600',
      deduction: 'text-red-600',
      refund: 'text-blue-600',
      bonus: 'text-purple-600',
      adjustment: 'text-gray-600'
    };
    return colors[type] || 'text-gray-600';
  };

  return (
    <AdminLayout>
      <Head title="Gestion des crédits" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des crédits</h1>
          <p className="mt-2 text-gray-600">
            Achetez des crédits pour recevoir des leads qualifiés
          </p>
        </div>

        {/* Current Balance */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Solde actuel</p>
              <p className="text-4xl font-bold text-gray-900">{agency.credits}</p>
              <p className="text-sm text-gray-500 mt-1">crédit{agency.credits > 1 ? 's' : ''} disponible{agency.credits > 1 ? 's' : ''}</p>
            </div>
            {agency.lowCredits && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                <p className="text-sm text-red-800 font-medium">⚠️ Crédits faibles</p>
                <p className="text-xs text-red-600">Seuil d'alerte: {agency.alertThreshold}</p>
              </div>
            )}
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
            <div>
              <p className="text-xs text-gray-500">Total acheté</p>
              <p className="text-lg font-semibold text-gray-900">{statistics.totalPurchased}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total dépensé</p>
              <p className="text-lg font-semibold text-gray-900">{statistics.totalSpent}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Moyenne/jour</p>
              <p className="text-lg font-semibold text-gray-900">{statistics.averageDailySpent.toFixed(1)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Jours restants estimés</p>
              <p className="text-lg font-semibold text-gray-900">
                {statistics.estimatedDaysRemaining === Infinity ? '∞' : statistics.estimatedDaysRemaining}
              </p>
            </div>
          </div>
        </div>

        {/* Credit Packs */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Packs de crédits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {creditPacks.map((pack) => (
              <div
                key={pack.id}
                className={`bg-white rounded-lg shadow-md p-6 border-2 ${
                  pack.name === 'Business' ? 'border-blue-500' : 'border-transparent'
                }`}
              >
                {pack.name === 'Business' && (
                  <span className="inline-block bg-blue-500 text-white text-xs px-2 py-1 rounded mb-2">
                    LE PLUS POPULAIRE
                  </span>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pack.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{pack.description}</p>

                <div className="mb-4">
                  <p className="text-3xl font-bold text-gray-900">{pack.price}€</p>
                  <p className="text-sm text-gray-500">{pack.pricePerCredit}€/crédit</p>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">{pack.credits}</span> crédits
                  </p>
                  {pack.bonusCredits > 0 && (
                    <p className="text-sm text-green-600 font-medium">
                      + {pack.bonusCredits} crédits bonus ({pack.bonusPercentage}%)
                    </p>
                  )}
                  <p className="text-lg font-bold text-blue-600 mt-2">
                    = {pack.totalCredits} crédits
                  </p>
                </div>

                <button
                  onClick={() => handlePurchase(pack.id)}
                  disabled={purchaseLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {purchaseLoading ? 'Traitement...' : 'Acheter'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Auto-Recharge */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recharge automatique</h2>
          <p className="text-gray-600 text-sm mb-4">
            Rechargez automatiquement vos crédits lorsque votre solde passe sous le seuil d'alerte
          </p>

          <div className="space-y-4">
            <Checkbox
              label="Activer la recharge automatique"
              name="auto-recharge"
              checked={autoRechargeEnabled}
              onChange={(e) => setAutoRechargeEnabled(e.target.checked)}
            />

            {autoRechargeEnabled && (
              <Select
                label="Pack de recharge automatique"
                name="auto_recharge_pack"
                value={autoRechargePack || ''}
                onChange={(e) => setAutoRechargePack(e.target.value)}
                placeholder="Sélectionner un pack"
                options={creditPacks.map((pack) => ({
                  value: pack.id,
                  label: `${pack.name} - ${pack.totalCredits} crédits (${pack.price}€)`
                }))}
              />
            )}

            <Button
              onClick={handleAutoRechargeUpdate}
              variant="primary"
            >
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Credit History */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Historique</h2>

          {/* Filters */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Filtrer par période</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {/* Period Filter */}
              <Select
                label="P&eacute;riode"
                name="period"
                value={selectedPeriod}
                onChange={(e) => applyPeriodFilter(e.target.value)}
                options={[
                  { value: '', label: 'Toutes les p\u00e9riodes' },
                  { value: '7d', label: '7 derniers jours' },
                  { value: '30d', label: '30 derniers jours' },
                  { value: '90d', label: '90 derniers jours' },
                  { value: 'custom', label: 'Personnalis\u00e9e' }
                ]}
              />

              {/* Start Date */}
              {selectedPeriod === 'custom' && (
                <>
                  <Input
                    label="Date d&eacute;but"
                    type="date"
                    name="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />

                  <Input
                    label="Date fin"
                    type="date"
                    name="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={applyCustomDateFilter}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      Appliquer
                    </button>
                  </div>
                </>
              )}

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Solde après
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {creditHistory.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      Aucun mouvement pour le moment
                    </td>
                  </tr>
                ) : (
                  creditHistory.map((movement) => (
                    <tr key={movement.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(movement.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`font-medium ${getMovementTypeColor(movement.movementType)}`}>
                          {getMovementTypeLabel(movement.movementType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {movement.description}
                        {movement.lead && (
                          <span className="text-gray-500 ml-1">
                            - {movement.lead.fullName}
                          </span>
                        )}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        movement.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {movement.amount > 0 ? '+' : ''}{movement.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {movement.balanceAfter}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
